import { useState, useMemo } from "react";
import axios from "axios";
import { format } from "date-fns";
import {
  CalendarIcon, Check, ChevronLeft, ChevronRight, UploadCloud, X, Plus,
  Loader2, CheckCircle2, AlertCircle, PartyPopper, Home, UserPlus,
  BedDouble, Hotel, Languages,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { T, PHASES } from "@/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialData = {
  full_name: "", gender: "", age: "", dob: null, mobile: "", email: "",
  address: "", state: "", country: "India",
  church_name: "", designation: "", designation_other: "", reference: "",
  attending_as_family: "", family_who: { spouse: false, children: false, parents: false }, members: [],
  payment_completed: "", proof_path: "", proof_name: "", stay_proof_path: "", stay_proof_name: "",
  from_outside: "", needs_accommodation: "", accommodation_type: "", arrival: "", departure: "",
  acknowledged: false,
};

const inputCls = "h-12 border-white/15 bg-white/5 text-white placeholder:text-white/30 focus-visible:ring-[#FFE500]/50";
const labelCls = "text-sm font-semibold text-white/85";
const errCls = "mt-1 text-xs text-red-400";

const Field = ({ label, kn, error, children, testId }) => (
  <div data-testid={testId}>
    <Label className={`${labelCls} ${kn ? "font-kn" : ""}`}>{label} <span className="text-[#FFE500]">*</span></Label>
    <div className="mt-1.5">{children}</div>
    {error && <p data-testid={`${testId}-error`} className={`${errCls} ${kn ? "font-kn" : ""}`}>{error}</p>}
  </div>
);

const YesNo = ({ value, onChange, lang, name }) => (
  <div className="flex gap-3" role="radiogroup" aria-label={name}>
    {["yes", "no"].map((v) => (
      <button
        key={v}
        type="button"
        data-testid={`${name}-${v}`}
        onClick={() => onChange(v)}
        aria-pressed={value === v}
        className={`flex-1 rounded-xl border px-4 py-3 font-display text-sm font-bold uppercase tracking-wider transition-all ${
          value === v
            ? "border-[#FFE500] bg-[#FFE500] text-[#0B1233]"
            : "border-white/20 bg-white/5 text-white/70 hover:border-[#FFE500]/50"
        }`}
      >
        {v === "yes" ? T.p3.yes[lang] : T.p3.no[lang]}
      </button>
    ))}
  </div>
);

export const RegistrationForm = () => {
  const [lang, setLang] = useState("en");
  const kn = lang === "kn";
  const [phase, setPhase] = useState(0);
  const [data, setData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [success, setSuccess] = useState(null);

  const set = (key, val) => setData((d) => ({ ...d, [key]: val }));

  const WHO_TO_RELATION = { spouse: "spouse", children: "child", parents: "parent" };

  const toggleWho = (key) => {
    const relation = WHO_TO_RELATION[key];
    setData((d) => {
      const turningOn = !d.family_who[key];
      const family_who = { ...d.family_who, [key]: turningOn };
      let members = d.members;
      if (turningOn) {
        members = [...members, { relation, name: "", age: "", gender: "" }];
      } else {
        members = members.filter((m) => m.relation !== relation);
      }
      return { ...d, family_who, members };
    });
  };

  const updateMember = (idx, field, value) =>
    setData((d) => {
      const members = [...d.members];
      members[idx] = { ...members[idx], [field]: value };
      return { ...d, members };
    });

  const addMember = (relation) =>
    setData((d) => ({ ...d, members: [...d.members, { relation, name: "", age: "", gender: "" }] }));

  const removeMember = (idx) =>
    setData((d) => ({ ...d, members: d.members.filter((_, i) => i !== idx) }));

  const renderMemberSection = (whoKey, relation, label, allowMultiple) => {
    if (!data.family_who[whoKey]) return null;
    const rows = data.members.map((m, idx) => ({ ...m, idx })).filter((m) => m.relation === relation);
    return (
      <div data-testid={`members-${whoKey}`} className="space-y-3 rounded-xl border border-white/10 bg-white/[0.03] p-4">
        <p className={`text-sm font-bold text-white/80 ${kn ? "font-kn" : "font-display uppercase tracking-wider"}`}>{label}</p>
        {rows.map((m, i) => (
          <div key={m.idx} data-testid={`${relation}-row-${i}`} className="flex flex-wrap items-end gap-3">
            <div className="min-w-[130px] flex-1">
              <Label className={`text-xs text-white/60 ${kn ? "font-kn" : ""}`}>{T.p3.memberName[lang]}</Label>
              <Input data-testid={`${relation}-name-${i}`} className={`${inputCls} mt-1`} value={m.name}
                onChange={(e) => updateMember(m.idx, "name", e.target.value)} />
            </div>
            <div className="w-20">
              <Label className={`text-xs text-white/60 ${kn ? "font-kn" : ""}`}>{T.p3.memberAge[lang]}</Label>
              <Input data-testid={`${relation}-age-${i}`} type="number" min="0" max="120" inputMode="numeric"
                className={`${inputCls} mt-1`} value={m.age} onChange={(e) => updateMember(m.idx, "age", e.target.value)} />
            </div>
            <div>
              <Label className={`text-xs text-white/60 ${kn ? "font-kn" : ""}`}>{T.p3.memberGender[lang]}</Label>
              <div className="mt-1 flex gap-1.5">
                {["male", "female"].map((g) => (
                  <button key={g} type="button" data-testid={`${relation}-gender-${i}-${g}`} aria-pressed={m.gender === g}
                    onClick={() => updateMember(m.idx, "gender", g)}
                    className={`rounded-lg border px-3 py-3 text-xs font-bold uppercase transition-all ${
                      m.gender === g ? "border-[#FFE500] bg-[#FFE500] text-[#0B1233]" : "border-white/20 bg-white/5 text-white/60 hover:border-[#FFE500]/50"
                    }`}>
                    {g === "male" ? T.p3.male[lang] : T.p3.female[lang]}
                  </button>
                ))}
              </div>
            </div>
            {allowMultiple && rows.length > 1 && (
              <button type="button" data-testid={`${relation}-remove-${i}`} aria-label="Remove"
                onClick={() => removeMember(m.idx)}
                className="flex h-12 w-10 items-center justify-center rounded-md border border-white/15 text-white/50 hover:border-red-400 hover:text-red-400">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
        {allowMultiple && (
          <button type="button" data-testid={`add-${relation}-btn`} onClick={() => addMember(relation)}
            className={`flex items-center gap-2 rounded-full border border-[#FFE500]/40 px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#FFE500] transition-colors hover:bg-[#FFE500] hover:text-[#0B1233] ${kn ? "font-kn normal-case tracking-normal" : ""}`}>
            <Plus className="h-3.5 w-3.5" /> {relation === "child" ? T.p3.addChild[lang] : T.p3.addParent[lang]}
          </button>
        )}
      </div>
    );
  };

  const validatePhase = (p) => {
    const e = {};
    const req = T.errors.required[lang];
    if (p === 0) {
      if (!data.full_name.trim()) e.full_name = req;
      if (!data.gender) e.gender = req;
      const age = parseInt(data.age, 10);
      if (!data.age || isNaN(age) || age < 1 || age > 120) e.age = T.errors.invalidAge[lang];
      if (!data.dob) e.dob = req;
      const digits = data.mobile.replace(/[\s+\-]/g, "");
      if (!/^\d{10,13}$/.test(digits)) e.mobile = T.errors.invalidMobile[lang];
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(data.email.trim())) e.email = T.errors.invalidEmail[lang];
      if (!data.address.trim()) e.address = req;
      if (!data.state.trim()) e.state = req;
      if (!data.country.trim()) e.country = req;
    }
    if (p === 1) {
      if (!data.church_name.trim()) e.church_name = req;
      if (!data.designation) e.designation = req;
      if (data.designation === "Other" && !data.designation_other.trim()) e.designation_other = req;
    }
    if (p === 2) {
      if (!data.attending_as_family) e.attending_as_family = req;
      if (data.attending_as_family === "yes") {
        if (!data.family_who.spouse && !data.family_who.children && !data.family_who.parents) e.family_who = req;
        const incomplete = data.members.some((m) => !m.name.trim() || !String(m.age).trim() || !m.gender);
        if (data.members.length === 0 || incomplete) e.members = req;
      }
    }
    if (p === 3) {
      if (!data.payment_completed) e.payment_completed = req;
      if (!data.proof_path) e.proof = req;
    }
    if (p === 4) {
      if (!data.from_outside) e.from_outside = req;
      if (!data.needs_accommodation) e.needs_accommodation = req;
      if (data.needs_accommodation === "yes" && !data.accommodation_type) e.accommodation_type = req;
      if (data.accommodation_type === "dormitory" && !data.stay_proof_path) e.stay_proof = req;
    }
    if (p === 5) {
      if (!data.acknowledged) e.acknowledged = req;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const goNext = () => {
    if (!validatePhase(phase)) return;
    setPhase((p) => Math.min(p + 1, 5));
    document.getElementById("register")?.scrollIntoView({ behavior: "smooth" });
  };
  const goBack = () => {
    setErrors({});
    setPhase((p) => Math.max(p - 1, 0));
    document.getElementById("register")?.scrollIntoView({ behavior: "smooth" });
  };

  const handleFile = (pathKey, nameKey, errKey) => async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setErrors((er) => ({ ...er, [errKey]: undefined }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/uploads/payment-proof`, fd);
      setData((d) => ({ ...d, [pathKey]: res.data.path, [nameKey]: res.data.filename }));
    } catch (err) {
      setErrors((er) => ({ ...er, [errKey]: err.response?.data?.detail || T.errors.uploadFailed[lang] }));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!validatePhase(5)) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const payload = {
        language: lang,
        personal: {
          full_name: data.full_name, gender: data.gender, age: data.age,
          dob: data.dob ? format(data.dob, "dd/MM/yyyy") : "",
          mobile: data.mobile, email: data.email, address: data.address,
          state: data.state, country: data.country,
        },
        church: {
          church_name: data.church_name, designation: data.designation,
          designation_other: data.designation_other, reference: data.reference,
        },
        family: {
          attending_as_family: data.attending_as_family,
          members: data.attending_as_family === "yes" ? data.members : [],
        },
        payment: {
          payment_completed: data.payment_completed,
          payment_proof_path: data.proof_path || null,
          proof_original_filename: data.proof_name || null,
          stay_proof_path: data.stay_proof_path || null,
          stay_proof_original_filename: data.stay_proof_name || null,
        },
        travel: {
          from_outside: data.from_outside, needs_accommodation: data.needs_accommodation,
          accommodation_type: data.needs_accommodation === "yes" ? data.accommodation_type : null,
          arrival: data.arrival, departure: data.departure,
        },
        declaration: { acknowledged: data.acknowledged },
      };
      const fd = new FormData();
      fd.append("payload", JSON.stringify(payload));
      const res = await axios.post(`${API}/registrations`, fd);
      setSuccess(res.data);
    } catch (err) {
      setSubmitError(err.response?.data?.detail || T.errors.submitFailed[lang]);
    } finally {
      setSubmitting(false);
    }
  };

  const progressPct = useMemo(() => Math.round(((phase + 1) / 6) * 100), [phase]);
  const totalCandidates = 1 + (data.attending_as_family === "yes" ? data.members.length : 0);
  const primaryName = data.full_name.trim() || "Your Full Name";

  /* ---------------- success screen ---------------- */
  if (success) {
    return (
      <section id="register" data-testid="registration-success" className="relative py-20 sm:py-28">
        <div className="gc-waves" />
        <div className="relative z-10 mx-auto max-w-2xl px-4 text-center sm:px-6">
          <div className="gc-card-sheen gc-fade-up rounded-3xl p-8 sm:p-12">
            <PartyPopper className="mx-auto h-14 w-14 text-[#FFE500]" />
            <h2 data-testid="success-title" className={`mt-5 font-display text-3xl font-bold uppercase text-white sm:text-4xl ${kn ? "font-kn normal-case" : ""}`}>
              {T.success.title[lang]}
            </h2>
            <p className={`mt-3 text-sm uppercase tracking-[0.3em] text-white/50 ${kn ? "font-kn normal-case tracking-normal" : ""}`}>
              {T.success.ref[lang]}
            </p>
            <p data-testid="success-ref-number" className="mt-3 font-display text-4xl font-bold tracking-wider text-[#FFE500] sm:text-5xl">
              {success.registration_id}
            </p>
            <div className="mx-auto mt-8 max-w-md space-y-3 text-left">
              {[T.success.note1, T.success.note2, T.success.note3].map((n, i) => (
                <div key={i} data-testid={`success-note-${i}`} className="flex items-start gap-3 rounded-xl bg-white/[0.04] p-4">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#FFE500]" />
                  <p className={`text-sm text-white/85 ${kn ? "font-kn" : ""}`}>{n[lang]}</p>
                </div>
              ))}
            </div>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <button
                data-testid="register-another-btn"
                onClick={() => { setData(initialData); setPhase(0); setSuccess(null); setErrors({}); }}
                className="inline-flex items-center gap-2 rounded-full bg-[#FFE500] px-7 py-3 font-display text-sm font-bold uppercase tracking-wider text-[#0B1233] transition-transform hover:scale-105"
              >
                <UserPlus className="h-4 w-4" /> {T.success.registerAnother[lang]}
              </button>
              <a
                data-testid="back-home-btn"
                href="#top"
                className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors hover:border-[#FFE500] hover:text-[#FFE500]"
              >
                <Home className="h-4 w-4" /> {T.success.backHome[lang]}
              </a>
            </div>
          </div>
        </div>
      </section>
    );
  }

  /* ---------------- wizard ---------------- */
  return (
    <section id="register" data-testid="registration-section" className="relative py-20 sm:py-28">
      <div className="gc-waves" />
      <div className="relative z-10 mx-auto max-w-3xl px-4 sm:px-6">
        <p data-testid="form-sub" className={`text-center text-sm uppercase tracking-[0.3em] text-[#FFE500] ${kn ? "font-kn normal-case tracking-normal" : ""}`}>
          {T.form.sub[lang]}
        </p>
        <h2 data-testid="form-heading" className={`mt-3 text-center font-display text-4xl font-bold uppercase text-white sm:text-5xl ${kn ? "font-kn normal-case" : ""}`}>
          {T.form.heading[lang]}
        </h2>

        {/* Form language selector */}
        <div className="mt-6 flex items-center justify-center gap-3">
          <Languages className="h-4 w-4 text-[#FFE500]" />
          <span data-testid="form-lang-label" className={`text-xs uppercase tracking-[0.25em] text-white/50 ${kn ? "font-kn normal-case tracking-normal" : ""}`}>
            {T.p5.selectLanguage[lang]}
          </span>
          <div data-testid="form-lang-toggle" className="flex rounded-full border border-white/15 bg-white/5 p-1">
            <button type="button" data-testid="form-lang-en" onClick={() => setLang("en")}
              className={`rounded-full px-4 py-1.5 text-xs font-bold transition-all ${lang === "en" ? "bg-[#FFE500] text-[#0B1233]" : "text-white/60 hover:text-white"}`}>
              English
            </button>
            <button type="button" data-testid="form-lang-kn" onClick={() => setLang("kn")}
              className={`font-kn rounded-full px-4 py-1.5 text-xs font-bold transition-all ${lang === "kn" ? "bg-[#FFE500] text-[#0B1233]" : "text-white/60 hover:text-white"}`}>
              ಕನ್ನಡ
            </button>
          </div>
        </div>

        {/* Progress */}
        <div className="mt-10">
          <div className="flex items-center justify-between text-xs text-white/60">
            <span data-testid="progress-label" className={kn ? "font-kn" : ""}>
              {T.form.stepOf[lang]} {phase + 1} {T.form.of[lang]} 6 — <span className="font-semibold text-[#FFE500]">{PHASES[phase][lang]}</span>
            </span>
            <span data-testid="progress-percent">{progressPct}%</span>
          </div>
          <div data-testid="progress-bar-track" className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10">
            <div data-testid="progress-bar-fill" className="h-full rounded-full bg-[#FFE500] transition-all duration-500" style={{ width: `${progressPct}%` }} />
          </div>
          <div className="mt-3 hidden justify-between sm:flex">
            {PHASES.map((p, i) => (
              <div key={i} data-testid={`progress-step-${i}`} className="flex flex-col items-center gap-1">
                <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold ${
                  i < phase ? "bg-[#FFE500] text-[#0B1233]" : i === phase ? "border-2 border-[#FFE500] text-[#FFE500]" : "border border-white/20 text-white/30"
                }`}>
                  {i < phase ? <Check className="h-3 w-3" /> : i + 1}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div data-testid={`phase-panel-${phase}`} className="gc-card-sheen mt-8 rounded-3xl p-6 sm:p-10">
          {/* ---------------- PHASE 1 ---------------- */}
          {phase === 0 && (
            <div className="space-y-5">
              <h3 data-testid="phase1-title" className={`font-display text-2xl font-bold uppercase text-white ${kn ? "font-kn normal-case" : ""}`}>{T.p1.title[lang]}</h3>
              <Field testId="field-full-name" label={T.p1.fullName[lang]} kn={kn} error={errors.full_name}>
                <Input data-testid="input-full-name" className={inputCls} value={data.full_name} onChange={(e) => set("full_name", e.target.value)} autoComplete="name" />
              </Field>
              <Field testId="field-gender" label={T.p1.gender[lang]} kn={kn} error={errors.gender}>
                <div className="flex gap-3" role="radiogroup" aria-label="Gender">
                  {["male", "female"].map((g) => (
                    <button key={g} type="button" data-testid={`gender-${g}`} aria-pressed={data.gender === g}
                      onClick={() => set("gender", g)}
                      className={`flex-1 rounded-xl border px-4 py-3 font-display text-sm font-bold uppercase tracking-wider transition-all ${
                        data.gender === g ? "border-[#FFE500] bg-[#FFE500] text-[#0B1233]" : "border-white/20 bg-white/5 text-white/70 hover:border-[#FFE500]/50"
                      }`}>
                      {g === "male" ? T.p1.male[lang] : T.p1.female[lang]}
                    </button>
                  ))}
                </div>
              </Field>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field testId="field-age" label={T.p1.age[lang]} kn={kn} error={errors.age}>
                  <Input data-testid="input-age" type="number" min="1" max="120" inputMode="numeric" className={inputCls} value={data.age} onChange={(e) => set("age", e.target.value)} />
                </Field>
                <Field testId="field-dob" label={T.p1.dob[lang]} kn={kn} error={errors.dob}>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button data-testid="input-dob" type="button"
                        className="flex h-12 w-full items-center justify-between rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white hover:border-[#FFE500]/50">
                        <span className={data.dob ? "" : "text-white/30"}>
                          {data.dob ? format(data.dob, "dd/MM/yyyy") : T.p1.pickDate[lang]}
                        </span>
                        <CalendarIcon className="h-4 w-4 text-[#FFE500]" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto border-white/15 bg-[#111B45] p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={data.dob}
                        onSelect={(d) => set("dob", d)}
                        captionLayout="dropdown-buttons"
                        fromYear={1930}
                        toYear={2026}
                        defaultMonth={data.dob || new Date(1995, 0)}
                        className="text-white [&_.rdp-day_selected]:bg-[#FFE500] [&_.rdp-day_selected]:text-[#0B1233]"
                      />
                    </PopoverContent>
                  </Popover>
                </Field>
              </div>
              <Field testId="field-mobile" label={T.p1.mobile[lang]} kn={kn} error={errors.mobile}>
                <Input data-testid="input-mobile" type="tel" inputMode="tel" className={inputCls} value={data.mobile} onChange={(e) => set("mobile", e.target.value)} autoComplete="tel" placeholder="+91" />
              </Field>
              <Field testId="field-email" label={T.p1.email[lang]} kn={kn} error={errors.email}>
                <Input data-testid="input-email" type="email" inputMode="email" className={inputCls} value={data.email} onChange={(e) => set("email", e.target.value)} autoComplete="email" />
              </Field>
              <Field testId="field-address" label={T.p1.address[lang]} kn={kn} error={errors.address}>
                <Textarea data-testid="input-address" className="min-h-[90px] border-white/15 bg-white/5 text-white placeholder:text-white/30 focus-visible:ring-[#FFE500]/50" value={data.address} onChange={(e) => set("address", e.target.value)} autoComplete="street-address" />
              </Field>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field testId="field-state" label={T.p1.state[lang]} kn={kn} error={errors.state}>
                  <Input data-testid="input-state" className={inputCls} value={data.state} onChange={(e) => set("state", e.target.value)} autoComplete="address-level1" />
                </Field>
                <Field testId="field-country" label={T.p1.country[lang]} kn={kn} error={errors.country}>
                  <Input data-testid="input-country" className={inputCls} value={data.country} onChange={(e) => set("country", e.target.value)} autoComplete="country-name" />
                </Field>
              </div>
            </div>
          )}

          {/* ---------------- PHASE 2 ---------------- */}
          {phase === 1 && (
            <div className="space-y-5">
              <h3 data-testid="phase2-title" className={`font-display text-2xl font-bold uppercase text-white ${kn ? "font-kn normal-case" : ""}`}>{T.p2.title[lang]}</h3>
              <Field testId="field-church-name" label={T.p2.churchName[lang]} kn={kn} error={errors.church_name}>
                <Input data-testid="input-church-name" className={inputCls} value={data.church_name} onChange={(e) => set("church_name", e.target.value)} />
              </Field>
              <Field testId="field-designation" label={T.p2.designation[lang]} kn={kn} error={errors.designation}>
                <div className="grid gap-2.5 sm:grid-cols-2" role="radiogroup" aria-label="Designation">
                  {T.p2.options.map((opt, i) => (
                    <button key={opt.en} type="button" data-testid={`designation-${i}`} aria-pressed={data.designation === opt.en}
                      onClick={() => set("designation", opt.en)}
                      className={`rounded-xl border px-4 py-3 text-left text-sm font-semibold transition-all ${
                        data.designation === opt.en ? "border-[#FFE500] bg-[#FFE500]/15 text-[#FFE500]" : "border-white/15 bg-white/5 text-white/70 hover:border-[#FFE500]/40"
                      } ${kn ? "font-kn" : ""}`}>
                      {opt[lang]}
                    </button>
                  ))}
                </div>
              </Field>
              {data.designation === "Other" && (
                <Field testId="field-designation-other" label={T.p2.otherSpecify[lang]} kn={kn} error={errors.designation_other}>
                  <Input data-testid="input-designation-other" className={inputCls} value={data.designation_other} onChange={(e) => set("designation_other", e.target.value)} />
                </Field>
              )}
              <div data-testid="field-reference">
                <Label className={`${labelCls} ${kn ? "font-kn" : ""}`}>{T.p2.reference[lang]}</Label>
                <div className="mt-1.5">
                  <Input data-testid="input-reference" className={inputCls} value={data.reference} onChange={(e) => set("reference", e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {/* ---------------- PHASE 3 ---------------- */}
          {phase === 2 && (
            <div className="space-y-6">
              <h3 data-testid="phase3-title" className={`font-display text-2xl font-bold uppercase text-white ${kn ? "font-kn normal-case" : ""}`}>{T.p3.title[lang]}</h3>
              <Field testId="field-family" label={T.p3.familyQ[lang]} kn={kn} error={errors.attending_as_family}>
                <YesNo name="family-attending" value={data.attending_as_family} onChange={(v) => set("attending_as_family", v)} lang={lang} />
              </Field>
              {data.attending_as_family === "yes" && (
                <div data-testid="family-members-box" className="space-y-5 rounded-2xl border border-[#FFE500]/20 bg-[#FFE500]/[0.04] p-5">
                  <p className={`text-sm font-semibold text-[#FFE500] ${kn ? "font-kn" : ""}`}>{T.p3.whoQ[lang]}</p>
                  <div className="flex flex-wrap gap-3">
                    {["spouse", "children", "parents"].map((rel) => (
                      <button key={rel} type="button" data-testid={`who-${rel}`} aria-pressed={data.family_who[rel]}
                        onClick={() => toggleWho(rel)}
                        className={`rounded-full border px-5 py-2.5 text-sm font-bold transition-all ${
                          data.family_who[rel]
                            ? "border-[#FFE500] bg-[#FFE500] text-[#0B1233]"
                            : "border-white/20 bg-white/5 text-white/70 hover:border-[#FFE500]/50"
                        } ${kn ? "font-kn" : "font-display uppercase tracking-wider"}`}>
                        {T.p3[rel][lang]}
                      </button>
                    ))}
                  </div>
                  {errors.family_who && <p data-testid="family-who-error" className={`${errCls} ${kn ? "font-kn" : ""}`}>{errors.family_who}</p>}
                  {(data.family_who.spouse || data.family_who.children || data.family_who.parents) && (
                    <p className={`text-xs text-white/55 ${kn ? "font-kn" : ""}`}>{T.p3.detailsNote[lang]}</p>
                  )}
                  {errors.members && <p data-testid="members-error" className={`${errCls} ${kn ? "font-kn" : ""}`}>{errors.members}</p>}
                  {renderMemberSection("spouse", "spouse", T.p3.spouse[lang], false)}
                  {renderMemberSection("children", "child", T.p3.children[lang], true)}
                  {renderMemberSection("parents", "parent", T.p3.parents[lang], true)}
                </div>
              )}
              <div data-testid="family-fee-reminder" className="flex items-start gap-3 rounded-xl border border-[#941D2C]/60 bg-[#941D2C]/15 p-4">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-[#FFE500]" />
                <p className={`text-sm text-white/85 ${kn ? "font-kn" : ""}`}>{T.p3.feeReminder[lang]}</p>
              </div>
            </div>
          )}

          {/* ---------------- PHASE 4 ---------------- */}
          {phase === 3 && (
            <div className="space-y-6">
              <h3 data-testid="phase4-title" className={`font-display text-2xl font-bold uppercase text-white ${kn ? "font-kn normal-case" : ""}`}>{T.p4.title[lang]}</h3>
              <div data-testid="payment-fee-box" className="rounded-2xl border border-[#FFE500]/25 bg-[#FFE500]/[0.06] p-5 text-center">
                <p data-testid="payment-fee-label" className={`font-display text-2xl font-bold text-[#FFE500] ${kn ? "font-kn" : ""}`}>{T.p4.feeLabel[lang]}</p>
                <p data-testid="payment-fee-includes" className={`mt-2 text-sm text-white/75 ${kn ? "font-kn" : ""}`}>{T.p4.feeIncludes[lang]}</p>
                <div data-testid="fee-breakdown" className="mx-auto mt-4 max-w-xs rounded-xl bg-white/[0.05] px-4 py-3">
                  <p className={`text-xs uppercase tracking-[0.2em] text-white/50 ${kn ? "font-kn normal-case tracking-normal" : ""}`}>
                    {T.p4.totalCandidates[lang]}: {totalCandidates}
                  </p>
                  <p data-testid="fee-total" className="mt-1 font-display text-3xl font-bold text-white">
                    ₹{(totalCandidates * 1000).toLocaleString("en-IN")}
                  </p>
                  <p className={`text-xs text-white/55 ${kn ? "font-kn" : ""}`}>{totalCandidates} × ₹1,000</p>
                </div>
              </div>
              <div data-testid="payment-qr-box" className="mx-auto max-w-sm text-center">
                <p className={`mb-3 text-sm font-semibold text-white/80 ${kn ? "font-kn" : ""}`}>{T.p4.scanHere[lang]}</p>
                {/* Original supplied UPI QR — displayed unmodified */}
                <img data-testid="payment-qr-image" src="/assets/payment-qr.png" alt="UPI payment QR code — Joel Joyson, UPI ID 9384161933@hdfc"
                  className="w-full rounded-2xl border border-white/10" />
                <p data-testid="payment-account-name" className="mt-3 text-sm text-white/70">
                  {T.p4.accountName[lang]}: <span className="font-bold uppercase text-[#FFE500]">Joel Joyson</span>
                </p>
              </div>
              <div data-testid="payment-mention-box" className="rounded-xl border border-[#FFE500]/25 bg-[#FFE500]/[0.05] p-4 text-center">
                <p className={`text-xs text-white/60 ${kn ? "font-kn" : ""}`}>{T.p4.mentionLabel[lang]}</p>
                <p data-testid="payment-mention-value" className="mt-1.5 font-display text-lg font-bold tracking-wide text-[#FFE500]">
                  Registration - {primaryName}
                </p>
              </div>
              <Field testId="field-payment-completed" label={T.p4.completedQ[lang]} kn={kn} error={errors.payment_completed}>
                <YesNo name="payment-completed" value={data.payment_completed} onChange={(v) => set("payment_completed", v)} lang={lang} />
              </Field>
              {(
                <Field testId="field-proof" label={T.p4.uploadLabel[lang]} kn={kn} error={errors.proof}>
                  <p className={`mb-2 text-xs text-white/50 ${kn ? "font-kn" : ""}`}>{T.p4.uploadHint[lang]} · <span className={`font-semibold text-[#FFE500] ${kn ? "font-kn" : ""}`}>{T.p4.uploadRequired[lang]}</span></p>
                  {data.proof_path ? (
                    <div data-testid="proof-uploaded" className="flex items-center justify-between rounded-xl border border-green-400/40 bg-green-400/10 p-4">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="h-5 w-5 text-green-400" />
                        <div>
                          <p className={`text-sm font-semibold text-white ${kn ? "font-kn" : ""}`}>{T.p4.uploaded[lang]}</p>
                          <p data-testid="proof-filename" className="text-xs text-white/60">{data.proof_name}</p>
                        </div>
                      </div>
                      <button type="button" data-testid="proof-remove-btn"
                        onClick={() => setData((d) => ({ ...d, proof_path: "", proof_name: "" }))}
                        className={`text-xs font-semibold text-red-400 hover:text-red-300 ${kn ? "font-kn" : ""}`}>
                        {T.p4.removeFile[lang]}
                      </button>
                    </div>
                  ) : (
                    <label data-testid="proof-upload-label"
                      className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-white/20 bg-white/[0.03] p-8 transition-colors hover:border-[#FFE500]/50">
                      {uploading ? (
                        <>
                          <Loader2 className="h-8 w-8 animate-spin text-[#FFE500]" />
                          <span data-testid="proof-uploading" className={`text-sm text-white/60 ${kn ? "font-kn" : ""}`}>{T.p4.uploading[lang]}</span>
                        </>
                      ) : (
                        <>
                          <UploadCloud className="h-8 w-8 text-[#FFE500]" />
                          <span className={`text-sm text-white/60 ${kn ? "font-kn" : ""}`}>{T.p4.uploadLabel[lang]}</span>
                        </>
                      )}
                      <input data-testid="proof-file-input" type="file" accept="image/png,image/jpeg,image/webp,image/heic,image/heif,application/pdf"
                        className="hidden" onChange={handleFile("proof_path", "proof_name", "proof")} disabled={uploading} />
                    </label>
                  )}
                </Field>
              )}
              {data.payment_completed === "no" && (
                <div data-testid="pay-later-note" className="flex items-start gap-3 rounded-xl border border-[#941D2C]/60 bg-[#941D2C]/15 p-4">
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-[#FFE500]" />
                  <p className={`text-sm text-white/85 ${kn ? "font-kn" : ""}`}>{T.p4.payLaterNote[lang]}</p>
                </div>
              )}
            </div>
          )}

          {/* ---------------- PHASE 5 ---------------- */}
          {phase === 4 && (
            <div className="space-y-6">
              <h3 data-testid="phase5-title" className={`font-display text-2xl font-bold uppercase text-white ${kn ? "font-kn normal-case" : ""}`}>{T.p5.title[lang]}</h3>
              <p data-testid="phase5-welcome" className={`text-base text-[#FFE500]/90 ${kn ? "font-kn" : ""}`}>{T.p5.welcome[lang]}</p>
              <Field testId="field-from-outside" label={T.p5.outsideQ[lang]} kn={kn} error={errors.from_outside}>
                <YesNo name="from-outside" value={data.from_outside} onChange={(v) => set("from_outside", v)} lang={lang} />
              </Field>
              <Field testId="field-accommodation" label={T.p5.accomQ[lang]} kn={kn} error={errors.needs_accommodation}>
                <YesNo name="accommodation" value={data.needs_accommodation}
                  onChange={(v) => setData((d) => ({ ...d, needs_accommodation: v, accommodation_type: v === "yes" ? d.accommodation_type : "" }))}
                  lang={lang} />
              </Field>
              {data.needs_accommodation === "yes" && (
                <div data-testid="accommodation-options" className="space-y-4">
                  <Field testId="field-accommodation-type" label={T.p5.accomTypeQ[lang]} kn={kn} error={errors.accommodation_type}>
                    <div className="grid gap-3 sm:grid-cols-2" role="radiogroup" aria-label="Accommodation option">
                      <button type="button" data-testid="accom-dormitory" aria-pressed={data.accommodation_type === "dormitory"}
                        onClick={() => set("accommodation_type", "dormitory")}
                        className={`rounded-2xl border p-5 text-left transition-all ${
                          data.accommodation_type === "dormitory" ? "border-[#FFE500] bg-[#FFE500]/10" : "border-white/15 bg-white/5 hover:border-[#FFE500]/40"
                        }`}>
                        <BedDouble className="h-6 w-6 text-[#FFE500]" />
                        <p className={`mt-3 font-display text-base font-bold uppercase tracking-wide text-white ${kn ? "font-kn normal-case" : ""}`}>{T.p5.dorm[lang]}</p>
                        <p className={`mt-1 text-sm font-bold text-[#FFE500] ${kn ? "font-kn" : ""}`}>{T.p5.dormPrice[lang]}</p>
                        <p className={`mt-1.5 text-xs leading-relaxed text-white/60 ${kn ? "font-kn" : ""}`}>{T.p5.dormIncludes[lang]}</p>
                      </button>
                      <button type="button" data-testid="accom-hotel" aria-pressed={data.accommodation_type === "hotel"}
                        onClick={() => set("accommodation_type", "hotel")}
                        className={`rounded-2xl border p-5 text-left transition-all ${
                          data.accommodation_type === "hotel" ? "border-[#FFE500] bg-[#FFE500]/10" : "border-white/15 bg-white/5 hover:border-[#FFE500]/40"
                        }`}>
                        <Hotel className="h-6 w-6 text-[#FFE500]" />
                        <p className={`mt-3 font-display text-base font-bold uppercase tracking-wide text-white ${kn ? "font-kn normal-case" : ""}`}>{T.p5.hotel[lang]}</p>
                        <p className={`mt-1.5 text-xs leading-relaxed text-white/60 ${kn ? "font-kn" : ""}`}>{T.p5.hotelNote[lang]}</p>
                      </button>
                    </div>
                  </Field>
                  {data.accommodation_type === "hotel" && (
                    <div data-testid="hotel-note" className="flex items-start gap-3 rounded-xl bg-white/[0.04] p-4">
                      <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-[#FFE500]" />
                      <p className={`text-sm text-white/75 ${kn ? "font-kn" : ""}`}>{T.p5.hotelNote[lang]}</p>
                    </div>
                  )}
                  {data.accommodation_type === "dormitory" && (
                    <div data-testid="dorm-payment-box" className="space-y-4 rounded-2xl border border-[#FFE500]/25 bg-[#FFE500]/[0.05] p-5 text-center">
                      <p className={`font-display text-lg font-bold uppercase tracking-wide text-white ${kn ? "font-kn normal-case" : ""}`}>{T.p5.dormFeeTitle[lang]}</p>
                      <div>
                        <p data-testid="dorm-fee-total" className="font-display text-3xl font-bold text-[#FFE500]">
                          ₹{(totalCandidates * 1500).toLocaleString("en-IN")}
                        </p>
                        <p className={`mt-1 text-xs text-white/55 ${kn ? "font-kn" : ""}`}>{totalCandidates} × ₹1,500</p>
                      </div>
                      <img data-testid="dorm-qr-image" src="/assets/payment-qr.png" alt="UPI payment QR code — Joel Joyson"
                        className="mx-auto w-full max-w-[260px] rounded-xl border border-white/10" />
                      <p className="text-sm text-white/70">
                        {T.p4.accountName[lang]}: <span className="font-bold uppercase text-[#FFE500]">Joel Joyson</span>
                      </p>
                      <div className="rounded-xl border border-[#FFE500]/25 bg-white/[0.03] p-3">
                        <p className={`text-xs text-white/60 ${kn ? "font-kn" : ""}`}>{T.p4.mentionLabel[lang]}</p>
                        <p data-testid="stay-mention-value" className="mt-1 font-display text-base font-bold tracking-wide text-[#FFE500]">
                          Stay - {primaryName}
                        </p>
                      </div>
                      <div data-testid="stay-proof-field" className="text-left">
                        <Label className={`${labelCls} ${kn ? "font-kn" : ""}`}>{T.p5.stayUploadLabel[lang]} <span className="text-[#FFE500]">*</span></Label>
                        <p className={`mb-2 mt-1 text-xs text-white/50 ${kn ? "font-kn" : ""}`}>
                          {T.p4.uploadHint[lang]} · <span className={`font-semibold text-[#FFE500] ${kn ? "font-kn" : ""}`}>{T.p4.uploadRequired[lang]}</span>
                        </p>
                        {errors.stay_proof && <p data-testid="stay-proof-error" className={`${errCls} mb-2 ${kn ? "font-kn" : ""}`}>{errors.stay_proof}</p>}
                        {data.stay_proof_path ? (
                          <div data-testid="stay-proof-uploaded" className="flex items-center justify-between rounded-xl border border-green-400/40 bg-green-400/10 p-4">
                            <div className="flex items-center gap-3">
                              <CheckCircle2 className="h-5 w-5 text-green-400" />
                              <div>
                                <p className={`text-sm font-semibold text-white ${kn ? "font-kn" : ""}`}>{T.p4.uploaded[lang]}</p>
                                <p data-testid="stay-proof-filename" className="text-xs text-white/60">{data.stay_proof_name}</p>
                              </div>
                            </div>
                            <button type="button" data-testid="stay-proof-remove-btn"
                              onClick={() => setData((d) => ({ ...d, stay_proof_path: "", stay_proof_name: "" }))}
                              className={`text-xs font-semibold text-red-400 hover:text-red-300 ${kn ? "font-kn" : ""}`}>
                              {T.p4.removeFile[lang]}
                            </button>
                          </div>
                        ) : (
                          <label data-testid="stay-proof-upload-label"
                            className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-white/20 bg-white/[0.03] p-6 transition-colors hover:border-[#FFE500]/50">
                            {uploading ? (
                              <>
                                <Loader2 className="h-7 w-7 animate-spin text-[#FFE500]" />
                                <span className={`text-sm text-white/60 ${kn ? "font-kn" : ""}`}>{T.p4.uploading[lang]}</span>
                              </>
                            ) : (
                              <>
                                <UploadCloud className="h-7 w-7 text-[#FFE500]" />
                                <span className={`text-sm text-white/60 ${kn ? "font-kn" : ""}`}>{T.p5.stayUploadLabel[lang]}</span>
                              </>
                            )}
                            <input data-testid="stay-proof-file-input" type="file" accept="image/png,image/jpeg,image/webp,image/heic,image/heif,application/pdf"
                              className="hidden" onChange={handleFile("stay_proof_path", "stay_proof_name", "stay_proof")} disabled={uploading} />
                          </label>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {(data.from_outside === "yes" || data.needs_accommodation === "yes") && (
                <div className="grid gap-5 sm:grid-cols-2">
                  <div data-testid="field-arrival">
                    <Label className={`${labelCls} ${kn ? "font-kn" : ""}`}>{T.p5.arrival[lang]}</Label>
                    <div className="mt-1.5">
                      <Input data-testid="input-arrival" type="datetime-local" className={`${inputCls} [color-scheme:dark]`} value={data.arrival} onChange={(e) => set("arrival", e.target.value)} />
                    </div>
                  </div>
                  <div data-testid="field-departure">
                    <Label className={`${labelCls} ${kn ? "font-kn" : ""}`}>{T.p5.departure[lang]}</Label>
                    <div className="mt-1.5">
                      <Input data-testid="input-departure" type="datetime-local" className={`${inputCls} [color-scheme:dark]`} value={data.departure} onChange={(e) => set("departure", e.target.value)} />
                    </div>
                  </div>
                </div>
              )}
              <div data-testid="travel-note" className="flex items-start gap-3 rounded-xl bg-white/[0.04] p-4">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-[#FFE500]" />
                <p className={`text-sm text-white/75 ${kn ? "font-kn" : ""}`}>{T.p5.note[lang]}</p>
              </div>
            </div>
          )}

          {/* ---------------- PHASE 6 ---------------- */}
          {phase === 5 && (
            <div className="space-y-6">
              <h3 data-testid="phase6-title" className={`font-display text-2xl font-bold uppercase text-white ${kn ? "font-kn normal-case" : ""}`}>{T.p6.title[lang]}</h3>
              <div className="space-y-4">
                {T.p6.statements.map((s, i) => (
                  <div key={i} data-testid={`declaration-${i}`} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#FFE500] text-xs font-bold text-[#0B1233]">{i + 1}</span>
                      <div>
                        <p className="text-sm leading-relaxed text-white/90">{s.en}</p>
                        {kn && <p className="font-kn mt-2 text-sm leading-relaxed text-[#FFE500]/80">{s.kn}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div data-testid="ack-box" className="rounded-2xl border border-[#FFE500]/30 bg-[#FFE500]/[0.05] p-5">
                <p className={`mb-3 text-xs font-bold uppercase tracking-[0.25em] text-[#FFE500] ${kn ? "font-kn normal-case tracking-normal" : ""}`}>{T.p6.ackHeading[lang]}</p>
                <label className="flex cursor-pointer items-start gap-3">
                  <Checkbox data-testid="ack-checkbox" checked={data.acknowledged}
                    onCheckedChange={(v) => set("acknowledged", v === true)}
                    className="mt-0.5 h-5 w-5 border-white/40 data-[state=checked]:border-[#FFE500] data-[state=checked]:bg-[#FFE500] data-[state=checked]:text-[#0B1233]" />
                  <span className={`text-sm leading-relaxed text-white/90 ${kn ? "font-kn" : ""}`}>{T.p6.ack[lang]}</span>
                </label>
                {errors.acknowledged && <p data-testid="ack-error" className={`${errCls} mt-2 ${kn ? "font-kn" : ""}`}>{errors.acknowledged}</p>}
              </div>
              {submitError && (
                <div data-testid="submit-error" className="flex items-start gap-3 rounded-xl border border-red-400/50 bg-red-400/10 p-4">
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-400" />
                  <p className={`text-sm text-red-300 ${kn ? "font-kn" : ""}`}>{submitError}</p>
                </div>
              )}
            </div>
          )}

          {/* ---------------- nav buttons ---------------- */}
          <div className="mt-10 flex items-center justify-between gap-4">
            <Button
              data-testid="back-btn"
              type="button"
              variant="outline"
              onClick={goBack}
              disabled={phase === 0 || submitting}
              className="h-12 rounded-full border-white/20 bg-transparent px-6 text-white hover:border-[#FFE500]/50 hover:bg-white/5 hover:text-[#FFE500] disabled:opacity-30"
            >
              <ChevronLeft className="mr-1 h-4 w-4" /> {T.form.back[lang]}
            </Button>
            {phase < 5 ? (
              <Button
                data-testid="continue-btn"
                type="button"
                onClick={goNext}
                className="h-12 rounded-full bg-[#FFE500] px-8 font-display font-bold uppercase tracking-wider text-[#0B1233] hover:bg-[#FFE500]/90"
              >
                {T.form.continue[lang]} <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            ) : (
              <Button
                data-testid="submit-registration-btn"
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="h-12 rounded-full bg-[#FFE500] px-8 font-display font-bold uppercase tracking-wider text-[#0B1233] shadow-[0_0_25px_rgba(255,229,0,0.3)] hover:bg-[#FFE500]/90 disabled:opacity-50"
              >
                {submitting ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {T.form.submitting[lang]}</>
                ) : (
                  <><Check className="mr-2 h-4 w-4" /> {T.form.submit[lang]}</>
                )}
              </Button>
            )}
          </div>
        </div>
        <p data-testid="required-note" className={`mt-4 text-center text-xs text-white/40 ${kn ? "font-kn" : ""}`}>{T.form.requiredNote[lang]}</p>
      </div>
    </section>
  );
};
